const normalizeText = (value) => String(value || '').trim().toLowerCase();
const DEFAULT_CARREFOUR_PLU_CATEGORY = 'DIVERS LE CELADON';

const pushIfPresent = (target, value) => {
  const normalized = String(value || '').trim();
  if (normalized) {
    target.push(normalized);
  }
};

const collectProductCategoryNames = (product) => {
  const names = [];

  pushIfPresent(names, product?.categoryName);
  pushIfPresent(names, product?.category_name);
  pushIfPresent(names, product?.rayon);

  if (typeof product?.category === 'string') {
    pushIfPresent(names, product.category);
  } else {
    pushIfPresent(names, product?.category?.name);
    pushIfPresent(names, product?.category?.label);
  }

  if (Array.isArray(product?.categoryNames)) {
    product.categoryNames.forEach((entry) => pushIfPresent(names, entry));
  }

  if (Array.isArray(product?.categories)) {
    product.categories.forEach((entry) => {
      if (typeof entry === 'string') {
        pushIfPresent(names, entry);
        return;
      }

      pushIfPresent(names, entry?.name);
      pushIfPresent(names, entry?.label);
      pushIfPresent(names, entry?.category);
    });
  }

  return Array.from(new Set(names));
};

const resolvePluFromDetails = (product) => {
  const details = Array.isArray(product?.details) ? product.details : [];

  const preferredDetail = details.find((detail) => {
    const detailType = String(detail?.type || '').toLowerCase();
    const detailName = String(detail?.name || '').toLowerCase();
    return detailType.includes('plu') || detailName.includes('plu');
  });

  if (preferredDetail?.intern_code) {
    return String(preferredDetail.intern_code).trim();
  }

  const fallbackDetail = details.find((detail) => detail?.intern_code);
  if (fallbackDetail?.intern_code) {
    return String(fallbackDetail.intern_code).trim();
  }

  return '';
};

const resolvePluFromCategories = (product, pluCategories = []) => {
  const normalizedCategories = (Array.isArray(pluCategories) ? pluCategories : [])
    .map((entry) => ({
      plu: String(entry?.plu || '').trim(),
      category: String(entry?.category || '').trim(),
    }))
    .filter((entry) => entry.plu && entry.category)
    .map((entry) => ({
      ...entry,
      normalizedCategory: normalizeText(entry.category),
    }));

  if (!normalizedCategories.length) {
    return '';
  }

  const productCategoryNames = collectProductCategoryNames(product);

  for (const categoryName of productCategoryNames) {
    const normalizedName = normalizeText(categoryName);
    const match = normalizedCategories.find((entry) =>
      normalizedName === entry.normalizedCategory ||
      normalizedName.includes(entry.normalizedCategory) ||
      entry.normalizedCategory.includes(normalizedName)
    );

    if (match?.plu) {
      return match.plu;
    }
  }

  return '';
};

export const resolveDefaultCarrefourPlu = (pluCategories = []) => {
  const match = (Array.isArray(pluCategories) ? pluCategories : []).find(
    (entry) =>
      normalizeText(entry?.category) ===
      normalizeText(DEFAULT_CARREFOUR_PLU_CATEGORY)
  );

  return String(match?.plu || '').trim();
};

export const resolveStoredPlu = (product, pluCategories = []) =>
  resolvePluFromDetails(product) ||
  resolvePluFromCategories(product, pluCategories) ||
  resolveDefaultCarrefourPlu(pluCategories) ||
  '';

export const canUseCarrefourPluPrice = (value) => {
  const price = Number(value);
  return Number.isFinite(price) && price > 0 && price < 1000;
};

export const encodeCarrefourPluPrice = (value) => {
  if (!canUseCarrefourPluPrice(value)) {
    return '';
  }

  let integerPart = Math.floor(Number(value));
  let decimalPart = Math.round((Number(value) - integerPart) * 100);

  if (decimalPart >= 100) {
    integerPart += 1;
    decimalPart = 0;
  }

  if (integerPart < 0 || integerPart > 999) {
    return '';
  }

  const integerStr = String(integerPart).padStart(3, '0');
  const decimalStr = `${String(decimalPart).padStart(2, '0')}0`;

  return `${integerStr}${decimalStr}`;
};

export const buildCarrefourPluValue = (plu, value) => {
  const normalizedPlu = String(plu || '').trim();
  const encodedPrice = encodeCarrefourPluPrice(value);

  if (!normalizedPlu || !encodedPrice) {
    return '';
  }

  return `${normalizedPlu}${encodedPrice}`;
};

export const buildCarrefourPluTicket = ({
  plu,
  total,
  label,
  description,
} = {}) => {
  const numericTotal = Number(total);
  const qrValue = buildCarrefourPluValue(plu, numericTotal);

  if (!qrValue) {
    return null;
  }

  return {
    plu: String(plu || '').trim(),
    total: Number.isFinite(numericTotal) ? numericTotal : 0,
    label: String(label || description || 'PLU Carrefour').trim(),
    description: String(description || label || 'PLU Carrefour').trim(),
    qrValue,
  };
};
